
import * as  React from 'react';
import * as Images from 'pos/Images';
import * as MenuComposer from './MenuComposer';
import * as ProductComposition from '../ProductComposition';
import * as SalesStore from '../../SalesStore';

import { FlowAction, MenuRoleDTO, Permission, ProductForSaleDTO, Res, TicketLineDTO, ValueButtonSize } from 'core/api';
import { Events, Result, SessionProvider } from 'core/infrastructure';
import { AcceptActionButton, ActionButton, Content, Filler, Footer, Header, Keypad, KeypadActionButton, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { CatalogueFacade } from 'pos/facades';
import { CustomerDisplay } from 'pos/services';
import { Message, Modal, useOverlayExecutor } from 'core/ui-next/modal';
import { Box, HStack, Separator, VStack } from '@chakra-ui/react';
import { Command } from 'pos/common';
import { CompositionPanel, CurrentRoleProducts, getScrollTargetId, OtherRolesOptions, RoleTabs } from './MenuWizardScreen.Controls';
import { z } from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { zInt } from 'core/infrastructure-next';

type MenuWizardProps = { initialComposition: MenuComposer.Composition, buttonSize: ValueButtonSize } & Modal.Actions<void>;
type LocalQuantityByRole = Record<string, number>;

const initLocalQuantityByRole = (roles: { Name: string }[]): LocalQuantityByRole => {
	const acc: LocalQuantityByRole = {};
	roles.forEach(r => (acc[r.Name] = 0));
	return acc;
};

export async function composeProductForMenu(product: ProductForSaleDTO, { currentRole, menuHeader }: MenuComposer.Composition, quantity: number): Promise<Result<ProductComposition.ProductComposition[]>> {
	return Command.execute('composeProduct', product, quantity, { type: 'menu-item', preparationOrderId: currentRole.PreparationOrderId, groupName: currentRole.Name, ownerId: menuHeader.Id, quantity });
}

function getScrollTargetIdForRole(role: MenuRoleDTO, lastMenuLineAdded: Nullable<TicketLineDTO>) {

	// Si la última línea pertenece al role actual, scrollea a esa línea.
	// Si no, scrollea al header del role.
	const lastLineBelongsToRole = lastMenuLineAdded?.Product.GroupName === role.Name;
	const target = lastLineBelongsToRole ? lastMenuLineAdded : role;

	return getScrollTargetId(target);
}

const quantitySchema = z.object({
	qty: zInt({ nonNegative: true, messageInvalid: Res.Msg.MSG_INVALID_FORMAT() }).nullable()
});

type QuantityState = z.infer<typeof quantitySchema>;

export function MenuWizardScreen({ initialComposition, buttonSize, close }: MenuWizardProps) {

	const [showKeypad, setShowKeypad] = React.useState(false);
	const [composition, setComposition] = React.useState(initialComposition);
	const [selectedLine, selectLine] = React.useState<Nullable<TicketLineDTO>>(null);
	const [expandOptions, setExpandOptions] = React.useState(false);
	const [loadingOptions, setLoadingOption] = React.useState(false);

	const formRef = React.useRef<FormRef<QuantityState>>(null);

	const { execute } = useOverlayExecutor();

	// Scroll target derivado. Si cambia el role o la última línea añadido hago scroll
	const lastMenuLine = SalesStore.getLastMenuLine(composition.menuHeader);
	const scrollTargetId = React.useMemo(
		() => getScrollTargetIdForRole(composition.currentRole, lastMenuLine),
		[composition.currentRole, lastMenuLine]
	);

	React.useEffect(() => {
		document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [scrollTargetId]);

	const sendFocusToQty = React.useCallback(() => {
		setTimeout(() => formRef.current?.setFocus?.('qty'));
	}, []);

	const restoreKeyboardQuantity = React.useCallback(() => {
		formRef.current?.setValue('qty', null);
		sendFocusToQty();
	}, [sendFocusToQty]);

	React.useEffect(() => { showKeypad && restoreKeyboardQuantity(); }, [restoreKeyboardQuantity, showKeypad]);

	Events.useEventOn('pos:sale:ticket-prepared', async () => {
		if (SessionProvider.getCurrent().AfterPrepareTicketAction !== FlowAction.DoNothing) {
			// Cierro para aplicar AfterPrepareTicketAction
			close();
		}
	});

	// Cacheo localmente el número de platos que llevo introducidos localmente
	// para poder bloquear el UI cuando hace falta cambiar de role sin tener
	// que esperar a que me lleguen las actualizaciones de estado del servidor.
	const localQuantityByRoleRef = React.useRef<LocalQuantityByRole>(initLocalQuantityByRole(initialComposition.roles));

	const incLocalQty = React.useCallback((roleName: string) => {
		const qtyByRole = localQuantityByRoleRef.current;
		qtyByRole[roleName] = (qtyByRole[roleName] ?? 0) + 1;
	}, []);

	const restoreLocalQty = React.useCallback((roleName: string, composition: MenuComposer.Composition) => {
		const role = composition.roles.find(x => x.Name === roleName);
		if (!role)
			throw new Error(`Role ${roleName} not found!`);

		const total = MenuComposer.getRoleTotalQuantity(
			role,
			composition.menuHeader
		);
		const qtyByRole = localQuantityByRoleRef.current;
		qtyByRole[roleName] = total;
	}, []);

	const getLocalQty = React.useCallback((roleName: string) => {
		return localQuantityByRoleRef.current[roleName] ?? 0;
	}, []);

	const onShowKeypad = React.useCallback(async () => {
		setShowKeypad(v => !v);
	}, []);

	const isLastItemInRole = React.useCallback(() => {
		const serverTotal = MenuComposer.getRoleTotalQuantity(composition.currentRole, composition.menuHeader);
		const localPicked = getLocalQty(composition.currentRole.Name);
		const pendingServerSync = serverTotal - localPicked;
		return pendingServerSync === 1;
	}, [composition.currentRole, composition.menuHeader, getLocalQty]);

	// Renombrado para expresar que devuelve la next composition (y poder usarla si hiciera falta)
	const loadNextNotCompletedRole = React.useCallback(async (line: TicketLineDTO) => {

		// Como ha cambiado la composición, a lo mejor han completado el role. Trato de moverme 
		// si es posible. Si no forzará un render con la nueva composición
		const nextComposition = await MenuComposer.getNextNotCompletedRole(
			composition,
			SalesStore.getTicket,
			CatalogueFacade.getSaleableAsMenuItemByProductGroup
		);

		restoreLocalQty(line.Product.GroupName, nextComposition);
		setComposition(nextComposition);

		return nextComposition;

	}, [composition, restoreLocalQty]);


	const addLine = React.useCallback(
		async (compositionResult: Result<ProductComposition.ProductComposition[]>) => {

			if (!compositionResult.ok)
				return;

			const roleName = composition.currentRole.Name;
			incLocalQty(roleName);

			const addLineResult = await SalesStore.addLines(compositionResult.value);
			if (!addLineResult.ok)
				return;

			const lastLine = SalesStore.getLastMenuLine(composition.menuHeader)!;
			await CustomerDisplay.displayNewLine(SalesStore.getTicket(), lastLine);

			await loadNextNotCompletedRole(lastLine);
		},
		[composition, incLocalQty, loadNextNotCompletedRole]
	);

	const onEditLine = React.useCallback(async (line: TicketLineDTO) => {

		await execute({
			action: async () => {
				await Command.execute('editTicketLine', line, {
					showAddNotesOrEditMenu: true,
					showChangeAddins: true,
					showAdjustIngredients: true,
					showRemoveLine: true,
					showSetQuantity: true,
					showSetPreparationOrder: false,
					showSetTicketLineCreator: false,
					showPrepare: false,
					showProductDetails: false,
					showRequest: false,
					showSetDiscounts: false,
					showSetPrice: false,
					showEditSeatNumber: false
				});
			}
		});

		// Cuando cambia el ticket, porque editan una línea, refresco la composición.
		setComposition(await MenuComposer.refresh(composition, SalesStore.getTicket, CatalogueFacade.getSaleableAsMenuItemByProductGroup));

	}, [composition, execute]);

	const onDeleteLine = React.useCallback(async (line: TicketLineDTO) => {
		await Command.execute('removeTicketLine', line);
		// Cuando cambia el ticket, porque borran una línea, refresco la composición.
		setComposition(await MenuComposer.refresh(composition, SalesStore.getTicket, CatalogueFacade.getSaleableAsMenuItemByProductGroup));
	}, [composition]);

	const onProductSelected = React.useCallback(
		async (product: ProductForSaleDTO) => {

			const introducedQtyResult = formRef.current ? await formRef.current.getValue() : Result.ok({ qty: null });
			if (!introducedQtyResult.ok) {
				sendFocusToQty();
				return;
			}

			const introducedQty = introducedQtyResult.value.qty ?? 1;
			selectLine(null);

			if (!MenuComposer.newMenuItemCanBeAdded(composition, introducedQty, SalesStore.getTicket)) {
				await Message.show(Res.Msg.MSG_MENU_ITEM_QTY_LIMIT_REACHED(composition.currentRole.Name));
				sendFocusToQty();
				return;
			}

			// Distingo dos casos. Si voy a terminar el Role en base a lo que *YO* pienso localmente que ha picado
			// el usuario, bloqueo todo el proceso para esperar que se sincronice estado con el servidor antes de
			// pasar al siguiente role. Si todavía no termino el role, sólo bloqueo en caso de que sea necesario
			// para componer el producto ('on-first-request') y evito bloquear durante el addLine para que se
			// puedan picar rápidamente varias líneas dentro del mismo grupo.
			if (isLastItemInRole()) {
				await execute({
					overlay: 'on-start',
					waitForPendingRequests: true,
					action: async () => {
						const result = await composeProductForMenu(product, composition, introducedQty);
						return addLine(result);
					}
				});
			} else {
				const result = await execute({
					overlay: 'on-first-request',
					action: async () =>
						composeProductForMenu(product, composition, introducedQty)
				});
				await addLine(result);
			}

			restoreKeyboardQuantity();
		},
		[addLine, composition, execute, isLastItemInRole, restoreKeyboardQuantity, sendFocusToQty]
	);

	const onRoleSelected = React.useCallback(async (role: MenuRoleDTO) => {

		selectLine(null);

		const nextComposition = await MenuComposer.selectRole(
			composition,
			role,
			SalesStore.getTicket,
			CatalogueFacade.getSaleableAsMenuItemByProductGroup
		);

		setComposition(nextComposition);

	}, [composition]);

	const onSelectLine = React.useCallback(async (line: TicketLineDTO) => {
		selectLine(line);
	}, []);

	const onPrepareTicket = React.useCallback(async () => await Command.execute('prepareCurrentTicket'), []);

	const onShowMoreOptions = React.useCallback(async () => {
		// Si está abierto, cierro y no cargo nada
		if (expandOptions) {
			setExpandOptions(false);
			return;
		}

		try {

			setExpandOptions(true);
			setLoadingOption(true);

			const nextComposition = await MenuComposer.ensureProductsForAllRoles(
				composition,
				SalesStore.getTicket,
				CatalogueFacade.getSaleableAsMenuItemByProductGroup
			);

			setComposition(nextComposition);
		}
		finally {
			setLoadingOption(false);
		}
	}, [expandOptions, composition]);

	const { productsByRoleName, roles, currentRole } = composition;

	const otherRoles = React.useMemo(
		() => roles.filter(x => x.Name !== currentRole.Name),
		[roles, currentRole.Name]
	);

	const { Form, DecimalInput } = useControlsFor<QuantityState>();

	const [isCompositionVisible, setIsCompositionVisible] = React.useState(false);

	const toggleCompositionVisible = React.useCallback(() => setIsCompositionVisible(v => !v), []);

	return <PosScreen screenId='COMPOSE_MENU' size={{ width: 1000, height: 700 }} useKeyPad={false} >

		<Header title={Res.Web.COMPOSE_MENU()} onClose={close} wizardHeader={true} isCompositionVisible={toggleCompositionVisible} />

		<Content>
			{isCompositionVisible ?
				<VStack
					width={'full'}
					height="full"
					bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}
					minH={0}
					align="stretch"
					gap={'6'}
					padding={'2'}
				>
					<VStack flex="1" minH={0} overflow="auto" gap={'6'} padding={'0.5'}>
						<CompositionPanel
							header={composition.menuHeader}
							selectedRole={composition.currentRole}
							lines={SalesStore.getMenuLines(composition.menuHeader)}
							roles={composition.roles}
							selectedLine={selectedLine}
							onEditLine={onEditLine}
							onDeleteLine={onDeleteLine}
							onSelectLine={onSelectLine}
							width={'full'}
						/>
					</VStack>
				</VStack>
				:
				<VStack width="full" height="full" bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }}>
					<HStack width="full" height="full" paddingX="2" paddingY="3">
						<LargeDeviceOnly>
							<VStack minW={'79'} maxW={'79'} height={'full'} overflow={'auto'} gap={'6'}>
								<VStack width={'full'} height="full" minH={0} align="stretch" flexShrink={0}>
									<VStack flex="1" minH={0} overflow="auto" gap={'6'}>
										<CompositionPanel
											header={composition.menuHeader}
											selectedRole={composition.currentRole}
											lines={SalesStore.getMenuLines(composition.menuHeader)}
											roles={composition.roles}
											selectedLine={selectedLine}
											onEditLine={onEditLine}
											onDeleteLine={onDeleteLine}
											onSelectLine={onSelectLine}
											width={'full'}
										/>
									</VStack>
									{
										showKeypad
											? <Box flexShrink={0}>
												<Form initialValue={{ qty: null }} schema={quantitySchema} ref={formRef}>
													<DecimalInput linkTo='qty' placeholder='0' />
													<Keypad isFullScreen={false} isLargeDevice={true} />
												</Form>
											</Box>

											: null
									}
								</VStack>
							</VStack>
							<Separator height={'full'} orientation={'vertical'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} width={'1.25px'} />
						</LargeDeviceOnly>
						<VStack flexGrow={1} height={'full'} width={'full'} gap={'4'} overflow={'auto'}>
							<Box
								position="sticky"
								top={0}
								zIndex={1}
								width="full"
								flexShrink={0}
								display={'grid'}
								justifyContent={'space-around'}
							>
								<RoleTabs
									roles={composition.roles}
									currentRoleName={composition.currentRole.Name}
									onRoleSelected={onRoleSelected}
								/>
							</Box>
							<Separator width={'full'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} height={'1.25px'} />
							{/* minH=0 permite que este hijo flex encoja; overflowY gestiona después el scroll vertical. */}
							<VStack width="full" flex="1" minH={0} overflowY="auto" align="stretch" gap={'9'}>
								<CurrentRoleProducts
									role={composition.currentRole}
									productsByRoleName={productsByRoleName}
									buttonSize={buttonSize}
									onProductSelected={onProductSelected}
									lines={SalesStore.getMenuLines(composition.menuHeader)}
								/>
								{SessionProvider.hasPermission(Permission.Ticket_UseProductFromOtherRoleInMenu) &&
									<OtherRolesOptions
										key={currentRole.Name}
										expanded={expandOptions}
										loading={loadingOptions}
										currentRole={composition.currentRole}
										roles={otherRoles}
										productsByRoleName={productsByRoleName}
										buttonSize={buttonSize}
										onToggle={() => onShowMoreOptions()}
										onProductSelected={onProductSelected}
										lines={SalesStore.getMenuLines(composition.menuHeader)}
									/>
								}
							</VStack>
						</VStack>
					</HStack>
				</VStack>
			}
		</Content>

		<Footer>
			<LargeDeviceOnly>
				<KeypadActionButton action={onShowKeypad} />
				<Filler />
				<ActionButton image={Images.PrepareTicket} action={onPrepareTicket} />
				<AcceptActionButton action={async () => close()} />
			</LargeDeviceOnly>
			<PhoneOnly>
				<ActionButton image={Images.PrepareTicket} action={onPrepareTicket} />
				<Filler />
				<AcceptActionButton action={async () => close()} />
			</PhoneOnly>
		</Footer>

	</PosScreen>;
}
